import React from "react";
import { alpha } from "@mui/material/styles";
import Div from "@jumbo/shared/Div";
import { GlobalStyles, useTheme } from "@mui/material";

const CalendarWrapper = ({ children }) => {
    const theme = useTheme();

    return (
        <React.Fragment>
            <GlobalStyles
                styles={{
                    ".rbc-overlay .rbc-event": { backgroundColor: theme.palette.primary.main },
                }}
            />

            <Div
                sx={{
                    width: "100%",

                    ".rbc-toolbar": { display: "none" },

                    /* Toolbar styling - unchanged */
                    ".hc-toolbar": {
                        borderBottom: "1px solid #eef1f5",
                        paddingBottom: "14px",
                        marginBottom: "14px",
                    },
                    ".hc-toolbar-row": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "16px",
                        flexWrap: "wrap",
                        width: "100%",
                    },
                    ".hc-toolbar-left": {
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                    },
                    ".hc-toolbar-right": {
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                    },
                    ".hc-toolbar-title": {
                        fontWeight: 800,
                        fontSize: 20,
                        color: "#002465",
                        fontFamily: "'Nunito', sans-serif",
                        textTransform: "lowercase",
                        flex: "1 1 auto",
                        textAlign: "center",
                        minWidth: 220,
                        maxWidth: "55%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    },
                    ".hc-btn": {
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "10px",
                        border: "1px solid #e6eaf0",
                        color: "#111827",
                        padding: "8px 14px",
                        boxShadow: "none",
                        backgroundColor: "#fff",
                        "&:hover": { backgroundColor: "#f7f8fb", borderColor: "#e6eaf0" },
                    },
                    ".hc-btn-active": {
                        borderColor: "#2f6fed !important",
                        color: "#2f6fed !important",
                        backgroundColor: "#fff !important",
                    },

                    [theme.breakpoints.down("md")]: {
                        ".hc-toolbar-title": {
                            order: 3,
                            width: "100%",
                            maxWidth: "100%",
                            textAlign: "left",
                            minWidth: "unset",
                            fontSize: 24,
                        },
                    },

                    /* Calendar surface */
                    ".rbc-month-view, .rbc-time-view, .rbc-agenda-view": {
                        border: "1px solid #d1d5db", // ✅ darker grey border
                        borderRadius: "12px",
                        overflow: "hidden",
                    },

                    ".rbc-header": {
                        padding: "12px 0",
                        color: "#6b7280",
                        fontWeight: 600,
                        textTransform: "lowercase",
                        borderBottom: "1px solid #d1d5db", // ✅ darker
                    },
                    ".rbc-header + .rbc-header": { borderLeft: "1px solid #d1d5db" },

                    ".rbc-month-row + .rbc-month-row": { borderTop: "1px solid #d1d5db" },
                    ".rbc-day-bg + .rbc-day-bg": { borderLeft: "1px solid #d1d5db" },
                    ".rbc-off-range-bg": { backgroundColor: "#fbfbfc" },

                    ".rbc-date-cell": {
                        padding: "10px 10px 0 0",
                        fontWeight: 700,
                        color: "#002465",
                        fontSize: 14,
                        fontFamily: "'Nunito', sans-serif",
                    },

                    /* Today cell stays white, only black border */
                    ".rbc-today": {
                        backgroundColor: "#ffffff",
                        position: "relative",
                        //cursor: "not-allowed",
                    },

                    ".rbc-month-view .rbc-day-bg.rbc-today::after": {
                        content: '""',
                        position: "absolute",
                        inset: "0px",
                        border: "2px solid #666666",
                        borderRadius: "4px",
                        pointerEvents: "none",
                        zIndex: 2,
                    },


                    /* ✅ FULL CELL selectable background (from dayPropGetter class) */
                    ".rbc-month-view .rbc-day-bg.hc-selectable-day": {
                        backgroundColor: "#e8f5e9",
                    },
                    ".rbc-month-view .rbc-day-bg.hc-selectable-day:hover": {
                        backgroundColor: "#c8e6c9",
                        cursor: "pointer",
                    },


                    ".rbc-month-view .rbc-day-bg.hc-selectable-day-light": {
                        backgroundColor: "#f1f9f2",
                    },
                    ".rbc-month-view .rbc-day-bg.hc-selectable-day-light:hover": {
                        backgroundColor: "#c8e6c9",
                        cursor: "pointer",
                    },

                    /* Month view safety */
                    ".rbc-month-view .rbc-row-content": { overflow: "hidden" },

                    /* =========================
                       Event chips (matches screenshots)
                       ========================= */

                    ".rbc-event, .rbc-day-slot .rbc-background-event": {
                        borderTop: "0",
                        borderRight: "0",
                        borderBottom: "0",
                        borderLeftWidth: "4px",     // ✅ keep left bar
                        borderLeftStyle: "solid",   // ✅ keep left bar
                        borderLeftColor: "rgba(0,0,0,0.25)", // fallback
                        borderRadius: "8px",
                        padding: "6px 12px",
                        margin: "6px 8px 0",
                        boxShadow: "none",
                        boxSizing: "border-box",
                        maxWidth: "calc(100% - 16px)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        "&:focus": { outline: "none" },
                    },

                    ".rbc-event-content": {
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: 1.1,
                    },

                    /* ✅ Booked: base color #59B2FF -> show light blue bg + dark blue left bar + dark text */
                    ".rbc-event[style*='background-color: rgb(89, 178, 255)'], .rbc-event[style*='background-color: #59B2FF']": {
                        backgroundColor: "#cfe9ff !important",
                        color: "#0b2a5b !important",
                        borderLeftColor: "#1d6ade !important",
                    },

                    /* ✅ Completed: base color #1D6ADE -> dark blue bg + darker left bar + white text */
                    ".rbc-event[style*='background-color: rgb(29, 106, 222)'], .rbc-event[style*='background-color: #1D6ADE']": {
                        backgroundColor: "#1d6ade !important",
                        color: "#ffffff !important",
                        borderLeftColor: "#0b2a5b !important",
                    },

                    /* ✅ Cancelled: base color #D22B2B -> light red/pink bg + red text + red left bar */
                    ".rbc-event[style*='background-color: rgb(210, 43, 43)'], .rbc-event[style*='background-color: #D22B2B']": {
                        backgroundColor: "#ffd3d9 !important",
                        color: "#b42318 !important",
                        borderLeftColor: "#d22b2b !important",
                    },

                    /* ✅ No-show: base color #F054B0 -> pink bg + red text + red left bar */
                    ".rbc-event[style*='background-color: rgb(240, 84, 176)'], .rbc-event[style*='background-color: #F054B0']": {
                        backgroundColor: "#ffd3d9 !important",
                        color: "#b42318 !important",
                        borderLeftColor: "#e11d48 !important",
                    },

                    /* Selection color */
                    ".rbc-slot-selection, .rbc-selected-cell": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.18),
                    },

                    ".rbc-show-more": {
                        backgroundColor: "transparent",
                        color: "#2f6fed",
                        fontWeight: 700,
                    },
                }}
            >
                {children}
            </Div>
        </React.Fragment>
    );
};

export default CalendarWrapper;
